import {
  bringForward,
  createEmptyGroup,
  duplicateLayer,
  getState,
  quantizeTime,
  removeAudioTrack,
  removeKeyframe,
  removeLayer,
  renameGroup,
  reorderLayer,
  sendBackward,
  setLayerGroup,
  setPlayhead,
  setSelectedAudioId,
  setSelectedId,
  setTimelineFps,
  setTimelineQuantize,
  setTimelineZoom,
  subscribe,
  toggleGroupCollapsed,
  toggleLayerVisible,
  ungroup,
  updateKeyframe,
  updateLayer,
} from '../store/project';
import { openContextMenu } from '../ui/ContextMenu';
import { buildLayerMenu, buildTimelineBarMenu, buildTimelineEmptyMenu } from '../ui/menus';
import { icon, type IconName } from '../ui/icons';
import { FULL_PALETTE, addCustomSwatch, getCustomSwatches } from '../ui/palette';
import type { Layer } from '../types';

const KIND_ICON: Record<string, IconName> = {
  text: 'kind-text',
  image: 'kind-image',
  video: 'kind-video',
  shape: 'kind-shape',
  drawing: 'kind-drawing',
};

const KF_DRAG_THRESHOLD_PX = 3;

interface BarDrag {
  layerId: string;
  mode: 'shift' | 'left' | 'right';
  /** Distance (in MS, project time) from layer.start to where the user grabbed. */
  grabOffsetMs: number;
  origStart: number;
  origEnd: number;
  bar: HTMLElement;
  pendingStart: number;
  pendingEnd: number;
}

interface KeyframeDrag {
  layerId: string;
  kfId: string;
  origT: number;
  startMouseX: number;
  marker: HTMLElement;
  bar: HTMLElement;
  pendingT: number;
  moved: boolean;
}

interface PlayheadDrag {
  startMouseX: number;
  origT: number;
  pendingT: number;
}

export class Timeline {
  private root: HTMLElement;
  private toolbar!: HTMLElement;
  private side!: HTMLElement;
  private tracksWrap!: HTMLElement;
  private tracks!: HTMLElement;
  private ruler!: HTMLElement;
  private playhead!: HTMLElement;
  private playheadHead!: HTMLElement;
  private barDrag: BarDrag | null = null;
  private kfDrag: KeyframeDrag | null = null;
  private phDrag: PlayheadDrag | null = null;
  // Jog shuttle is built once and reused across renders, otherwise the
  // mousedown closure ends up holding a detached `knob`/`label` after the
  // toolbar gets rebuilt mid-drag (setPlayhead → emit → render).
  private jogEl: HTMLElement | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.build();
    subscribe(() => this.render());
    this.attachInteraction();
  }

  // ─── Layout ───────────────────────────────────────────────────────────
  private build(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);

    this.toolbar = document.createElement('div');
    this.toolbar.className = 'tl-toolbar';

    this.side = document.createElement('div');
    this.side.className = 'tl-side';

    this.tracksWrap = document.createElement('div');
    this.tracksWrap.className = 'tl-tracks';

    this.ruler = document.createElement('div');
    this.ruler.className = 'tl-ruler';

    this.playhead = document.createElement('div');
    this.playhead.className = 'tl-playhead';
    this.playheadHead = document.createElement('div');
    this.playheadHead.className = 'tl-playhead-head';
    this.playheadHead.title = '재생 헤드 (드래그)';
    this.playhead.appendChild(this.playheadHead);

    this.tracks = document.createElement('div');
    this.tracks.className = 'tl-tracks-inner';

    this.tracksWrap.append(this.ruler, this.tracks, this.playhead);

    this.root.append(this.toolbar, this.side, this.tracksWrap);
  }

  // ─── Render ───────────────────────────────────────────────────────────
  private render(): void {
    const { project, selectedId, playhead, timelineZoom, timelineFps, timelineQuantize } = getState();
    const px = timelineZoom; // pixels per ms
    const totalPx = project.duration * px;

    // Toolbar (rebuilt on every render so values stay in sync)
    this.renderToolbar(timelineZoom, timelineFps, timelineQuantize);

    // Ruler — second labels + frame minor ticks
    this.ruler.replaceChildren();
    this.ruler.style.width = `${totalPx}px`;
    const frameMs = 1000 / timelineFps;
    const minorEveryPx = frameMs * px;
    const showFrames = minorEveryPx >= 4; // hide if too dense to be readable
    if (showFrames) {
      for (let t = 0; t <= project.duration; t += frameMs) {
        const m = document.createElement('div');
        m.className = 'tick minor';
        m.style.left = `${t * px}px`;
        this.ruler.appendChild(m);
      }
    }
    // Major (1s) labels
    for (let t = 0; t <= project.duration; t += 1000) {
      const tick = document.createElement('div');
      tick.className = 'tick major';
      tick.style.left = `${t * px}px`;
      tick.textContent = `${(t / 1000).toFixed(0)}s`;
      this.ruler.appendChild(tick);
    }
    // Half-second labels when zoomed in
    if (px >= 0.18) {
      for (let t = 500; t <= project.duration; t += 1000) {
        const tick = document.createElement('div');
        tick.className = 'tick half';
        tick.style.left = `${t * px}px`;
        tick.textContent = `${(t / 1000).toFixed(1)}s`;
        this.ruler.appendChild(tick);
      }
    }

    // ─── Build display rows: group rows + (uncollapsed) layer rows ───
    const groups = project.groups ?? [];
    const groupById = new Map(groups.map((g) => [g.id, g]));
    type DisplayRow =
      | { kind: 'layer'; layer: Layer }
      | { kind: 'group-collapsed'; group: typeof groups[number]; members: Layer[] }
      | { kind: 'group-header'; group: typeof groups[number] };

    // Walk layers from front (last) to back (first) to keep visual z-order.
    const reversed = [...project.layers].reverse();
    const rows: DisplayRow[] = [];
    const groupSeen = new Set<string>();
    for (const layer of reversed) {
      if (layer.groupId && groupById.has(layer.groupId)) {
        const g = groupById.get(layer.groupId)!;
        if (groupSeen.has(g.id)) continue;
        groupSeen.add(g.id);
        const members = reversed.filter((l) => l.groupId === g.id);
        if (g.collapsed) {
          rows.push({ kind: 'group-collapsed', group: g, members });
        } else {
          rows.push({ kind: 'group-header', group: g });
          for (const m of members) rows.push({ kind: 'layer', layer: m });
        }
      } else {
        rows.push({ kind: 'layer', layer });
      }
    }
    // Tail-pin any group that no layer claims (i.e. just-created empty
    // groups). Without this an empty group is invisible and there's no
    // drop target for the user to add layers into.
    for (const g of groups) {
      if (groupSeen.has(g.id)) continue;
      rows.push({ kind: 'group-header', group: g });
    }

    // Side rows
    this.side.replaceChildren();
    for (const r of rows) {
      if (r.kind === 'layer') {
        this.side.appendChild(this.buildSideRow(r.layer, r.layer.id === selectedId, project.layers));
      } else if (r.kind === 'group-header') {
        this.side.appendChild(this.buildGroupRow(r.group, false));
      } else {
        this.side.appendChild(this.buildGroupRow(r.group, true));
      }
    }

    // Tracks
    this.tracks.replaceChildren();
    this.tracks.style.width = `${totalPx}px`;
    for (const r of rows) {
      const row = document.createElement('div');
      row.className = 'tl-row';
      if (r.kind === 'layer') {
        row.appendChild(this.buildBar(r.layer, selectedId === r.layer.id, px));
      } else if (r.kind === 'group-header') {
        row.classList.add('tl-row-group');
      } else {
        // Collapsed group: stack ALL member bars in this single row, overlapping.
        for (const m of r.members) {
          const bar = this.buildBar(m, selectedId === m.id, px);
          bar.classList.add('in-collapsed-group');
          row.appendChild(bar);
        }
      }
      this.tracks.appendChild(row);
    }

    // ─── Audio rows (project.audioTracks) ───
    const audioTracks = project.audioTracks ?? [];
    const selectedAudioId = getState().selectedAudioId;
    let totalRowsForPlayhead = rows.length;
    for (const at of audioTracks) {
      const isSel = at.id === selectedAudioId;
      // Side row — same affordance set as a layer row (kind icon, name,
      // X delete button) plus mousedown→select so the inspector opens
      // before any potential parent click handler fires.
      const sideRow = document.createElement('div');
      sideRow.className = 'tl-row-label tl-row-audio' + (isSel ? ' selected' : '');
      sideRow.dataset.audioId = at.id;
      const selectThis = (ev?: Event): void => {
        ev?.stopPropagation();
        setSelectedAudioId(at.id);
      };
      sideRow.addEventListener('mousedown', selectThis);

      const ki = document.createElement('span');
      ki.className = 'tl-row-kind';
      ki.appendChild(icon('audio'));
      const nm = document.createElement('span');
      nm.className = 'tl-row-name';
      nm.textContent = at.label || (at.kind === 'youtube' ? 'YouTube' : at.kind);

      const actions = document.createElement('span');
      actions.className = 'tl-row-actions';
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'tl-row-act danger';
      del.title = '사운드 삭제';
      del.textContent = '✕';
      del.addEventListener('mousedown', (e) => e.stopPropagation());
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        if (window.confirm(`「${at.label || at.kind}」 사운드를 삭제할까요?`)) {
          if (selectedAudioId === at.id) setSelectedAudioId(null);
          removeAudioTrack(at.id);
        }
      });
      actions.appendChild(del);

      sideRow.append(ki, nm, actions);
      this.side.appendChild(sideRow);

      // Track row — bar spans 0..(songEnd-songStart) clamped to project.duration.
      const len = Math.min(project.duration, Math.max(0, at.songEnd - at.songStart));
      const trackRow = document.createElement('div');
      trackRow.className = 'tl-row';
      const bar = document.createElement('div');
      bar.className = 'tl-bar tl-bar-audio' + (isSel ? ' selected' : '');
      bar.dataset.audioId = at.id;
      bar.style.cursor = 'pointer';
      bar.style.left = '0';
      bar.style.width = `${len * px}px`;
      bar.addEventListener('mousedown', selectThis);
      const lbl = document.createElement('span');
      lbl.className = 'tl-bar-label';
      lbl.textContent = at.label || at.kind;
      bar.appendChild(lbl);
      trackRow.appendChild(bar);
      this.tracks.appendChild(trackRow);
      totalRowsForPlayhead += 1;
    }

    // Frame grid in the tracks pane (subtle vertical lines)
    if (showFrames) {
      const grid = document.createElement('div');
      grid.className = 'tl-frame-grid';
      grid.style.width = `${totalPx}px`;
      // Use a single repeating background instead of N elements (one per frame)
      grid.style.backgroundImage = `linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px)`;
      grid.style.backgroundSize = `${minorEveryPx}px 100%`;
      this.tracks.appendChild(grid);
    }
    // Second-line stronger marks
    const secGrid = document.createElement('div');
    secGrid.className = 'tl-second-grid';
    secGrid.style.width = `${totalPx}px`;
    secGrid.style.backgroundImage = `linear-gradient(to right, rgba(255,255,255,0.09) 1px, transparent 1px)`;
    secGrid.style.backgroundSize = `${1000 * px}px 100%`;
    this.tracks.appendChild(secGrid);

    // Playhead
    this.playhead.style.left = `${playhead * px}px`;
    this.playhead.style.height = `${24 + totalRowsForPlayhead * 28 + 4}px`;
  }

  private buildGroupRow(group: import('../types').LayerGroup, collapsed: boolean): HTMLElement {
    const r = document.createElement('div');
    r.className = 'tl-row-label tl-row-group';
    if (collapsed) r.classList.add('collapsed');
    r.title = group.name;

    const caret = document.createElement('span');
    caret.className = 'tl-group-caret';
    caret.textContent = collapsed ? '▸' : '▾';

    const name = document.createElement('span');
    name.className = 'tl-row-name';
    if (group.color) name.style.color = group.color;
    name.textContent = group.name;

    const actions = document.createElement('span');
    actions.className = 'tl-row-actions';
    const ung = document.createElement('button');
    ung.type = 'button';
    ung.className = 'tl-row-act';
    ung.title = '그룹 해제';
    ung.appendChild(icon('close'));
    ung.addEventListener('click', (e) => {
      e.stopPropagation();
      ungroup(group.id);
    });
    actions.append(ung);

    r.append(caret, name, actions);
    r.addEventListener('click', () => toggleGroupCollapsed(group.id));
    r.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openContextMenu(e, [
        { label: collapsed ? '펼치기' : '접기', onClick: () => toggleGroupCollapsed(group.id) },
        { label: '이름 변경', onClick: () => promptGroupRename(group.id, group.name) },
        {},
        { label: '그룹 해제', danger: true, onClick: () => ungroup(group.id) },
      ]);
    });
    // Accept dropped layers — sets their groupId to this group, so the user
    // can drag a layer row onto a group header to "put it in" the group.
    r.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      r.classList.add('drag-over');
    });
    r.addEventListener('dragleave', () => r.classList.remove('drag-over'));
    r.addEventListener('drop', (ev) => {
      ev.preventDefault();
      r.classList.remove('drag-over');
      const id = ev.dataTransfer?.getData('text/plain');
      if (!id) return;
      setLayerGroup(id, group.id);
    });
    return r;
  }

  private renderToolbar(zoom: number, fps: number, quantize: boolean): void {
    this.toolbar.replaceChildren();

    // ─── Reset playhead ───
    const reset = mkBtn('', () => setPlayhead(0), 'tl-tb-btn');
    reset.title = '재생 헤드를 처음(0초)으로';
    reset.appendChild(icon('skip-back'));
    this.toolbar.append(reset, sep());

    // ─── New empty group ───
    const groupBtn = mkBtn('+ 그룹', () => {
      createEmptyGroup();
    }, 'tl-tb-btn');
    groupBtn.title = '새 빈 그룹 만들기 — 좌측 라벨로 레이어를 드래그해서 넣어요';
    this.toolbar.append(groupBtn, sep());

    // ─── Zoom controls ───
    const zoomOut = mkBtn('', () => setTimelineZoom(zoom / 1.5), 'tl-tb-btn');
    zoomOut.appendChild(icon('minus', { title: '축소' }));
    const zoomIn = mkBtn('', () => setTimelineZoom(zoom * 1.5), 'tl-tb-btn');
    zoomIn.appendChild(icon('plus', { title: '확대' }));

    const zoomInfo = document.createElement('span');
    zoomInfo.className = 'tl-tb-info';
    zoomInfo.textContent = `${Math.round((zoom / 0.12) * 100)}%`;

    const zoomSlider = document.createElement('input');
    zoomSlider.type = 'range';
    zoomSlider.className = 'tl-tb-zoom';
    zoomSlider.min = '0.02';
    zoomSlider.max = '1.5';
    zoomSlider.step = '0.005';
    zoomSlider.value = String(zoom);
    zoomSlider.title = '타임라인 확대 / 축소';
    zoomSlider.addEventListener('input', () => setTimelineZoom(parseFloat(zoomSlider.value)));

    // ─── FPS ───
    const fpsLab = document.createElement('span');
    fpsLab.className = 'tl-tb-label';
    fpsLab.textContent = 'fps';
    const fpsSel = document.createElement('select');
    fpsSel.className = 'tl-tb-sel';
    for (const f of [12, 24, 30, 60, 120]) {
      const opt = document.createElement('option');
      opt.value = String(f);
      opt.textContent = String(f);
      if (f === fps) opt.selected = true;
      fpsSel.appendChild(opt);
    }
    fpsSel.addEventListener('change', () => setTimelineFps(parseInt(fpsSel.value, 10)));

    // ─── Quantize toggle ───
    const qBtn = mkBtn('', () => setTimelineQuantize(!quantize), `tl-tb-btn tl-tb-toggle ${quantize ? 'on' : ''}`);
    qBtn.appendChild(icon('magnet'));
    const qLab = document.createElement('span');
    qLab.textContent = '퀀타이즈';
    qBtn.appendChild(qLab);
    qBtn.title = quantize ? '퀀타이즈 ON — 프레임에 스냅' : '퀀타이즈 OFF — 자유롭게';

    // Build the jog once and reuse — the closures inside hold knob/label
    // refs, and rebuilding on every render would orphan an in-progress drag.
    if (!this.jogEl) this.jogEl = this.makeJogShuttle();
    const jog = this.jogEl;

    const spacer = document.createElement('div');
    spacer.style.flex = '1';

    const hint = document.createElement('span');
    hint.className = 'tl-tb-hint';
    // Cluster the modifier hints in one line on the right edge: Shift snaps
    // ad-hoc even when quantize is off, Ctrl+wheel is horizontal scroll.
    hint.textContent = 'Shift = 스냅 · 스크롤은 Ctrl + 휠';

    this.toolbar.append(
      zoomOut, zoomSlider, zoomIn, zoomInfo, sep(),
      fpsLab, fpsSel, sep(),
      qBtn, sep(),
      jog,
      spacer,
      hint,
    );
  }

  /** Jog / shuttle. Drag right = forward, left = reverse. Speed scales with
      distance from the grab point.

      Release behaviour:
        - was playing  → snap back to ×1 (main player keeps playing, jog idles)
        - was paused   → keep playing at the released speed (sustained jog).
                         The jog rAF stays alive until the user re-clicks the
                         track or drags back to the centre. */
  private makeJogShuttle(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'tl-jog';
    wrap.title = '재생 속도 — 잡고 좌/우로 끌어 조절. 재생 중엔 놓으면 ×1, 정지 중엔 그 속도로 계속 재생';

    const track = document.createElement('div');
    track.className = 'tl-jog-track';
    const center = document.createElement('div');
    center.className = 'tl-jog-center';
    const knob = document.createElement('div');
    knob.className = 'tl-jog-knob';
    track.append(center, knob);

    // Editable speed input — typing a number sets the speed directly. We
    // store both a "displayed" textual value (so "×" prefix never gets eaten
    // when the user is mid-type) and the underlying jog value.
    const label = document.createElement('input');
    label.className = 'tl-jog-label tl-jog-input';
    label.type = 'text';
    label.value = '×0';
    label.title = '재생 속도 — 숫자 입력 가능 (예: 2, -1.5)';
    // Allow typing a target multiplier; on commit we invert speedFor() to
    // pick the matching value position.
    const commitTyped = (): void => {
      const raw = label.value.replace(/[×x*]/gi, '').trim();
      const sp = parseFloat(raw);
      if (!Number.isFinite(sp)) {
        // Ignore garbage; sync() will refresh on the next pointer tick
        sync();
        return;
      }
      // Inverse of speedFor: |v| = (|sp|/6)^(2/3)
      const mag = Math.pow(Math.min(1, Math.abs(sp) / 6), 2 / 3);
      value = Math.sign(sp) * mag;
      if (sp === 0) {
        stopLoop();
        sync();
      } else {
        startLoop();
        sync();
      }
    };
    label.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitTyped();
        label.blur();
      }
    });
    label.addEventListener('change', commitTyped);
    label.addEventListener('blur', commitTyped);

    wrap.append(track, label);

    let value = 0; // -1..+1, where ±1 ≈ ×6 speed
    let raf: number | null = null;
    let lastTick = 0;
    let dragging = false;

    const speedFor = (v: number): number => Math.sign(v) * Math.pow(Math.abs(v), 1.5) * 6;

    const fmt = (sp: number): string => {
      if (Math.abs(sp) < 0.005) return '×0';
      const sign = sp < 0 ? '−' : '×';
      const abs = Math.abs(sp);
      // Two decimals for fine control, but trim trailing zero on integers.
      const s = abs.toFixed(2);
      return `${sign}${sign === '−' ? '×' + s : s}`;
    };

    const sync = (): void => {
      knob.style.left = `${50 + value * 50}%`;
      // Don't clobber the user's typing — only refresh when the input
      // doesn't currently have focus.
      if (document.activeElement !== label) {
        label.value = fmt(speedFor(value));
      }
    };

    const startLoop = (): void => {
      if (raf != null) return;
      lastTick = performance.now();
      const step = (now: number): void => {
        const dt = now - lastTick;
        lastTick = now;
        const sp = speedFor(value);
        if (sp !== 0) {
          const cur = getState().playhead;
          const dur = getState().project.duration;
          const next = Math.max(0, Math.min(dur, cur + sp * dt));
          setPlayhead(next);
        }
        raf = window.requestAnimationFrame(step);
      };
      raf = window.requestAnimationFrame(step);
    };
    const stopLoop = (): void => {
      if (raf != null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    };

    sync();

    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      const wasPlaying = getState().isPlaying;
      const startX = e.clientX;
      const halfTrack = track.getBoundingClientRect().width / 2 || 1;
      value = 0;
      sync();
      startLoop();

      const onMove = (ev: MouseEvent): void => {
        if (!dragging) return;
        value = Math.max(-1, Math.min(1, (ev.clientX - startX) / halfTrack));
        sync();
      };
      const onUp = (): void => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        dragging = false;
        if (wasPlaying) {
          // Was playing → snap back to ×1, hand off to the main player.
          stopLoop();
          value = 0;
          knob.style.left = '50%';
          label.value = '×1';
          return;
        }
        // Was paused → keep advancing the playhead at the released speed.
        // Stopping conditions: dragged back to ~0, or user clicks the track again.
        if (Math.abs(speedFor(value)) < 0.05) {
          stopLoop();
          value = 0;
          sync();
        }
        // else: leave raf running, knob and label show the held speed.
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    return wrap;
  }

  // ─── Side row ────────────────────────────────────────────────────────
  private buildSideRow(layer: Layer, selected: boolean, allLayers: Layer[]): HTMLElement {
    const r = document.createElement('div');
    r.className = 'tl-row-label';
    if (selected) r.classList.add('selected');
    if (layer.visible === false) r.classList.add('hidden-layer');
    r.draggable = true;
    r.dataset.id = layer.id;
    r.title = layer.name;

    // The kind icon doubles as a color chip — clicking it opens the color
    // sub-popover directly. Stays a <span> (not a <button>) so it doesn't
    // intercept the row's native drag-start when the user grabs from the
    // leftmost cell to drop the layer onto a group.
    const kind = document.createElement('span');
    kind.className = 'tl-row-kind tl-row-color-chip';
    kind.title = '색상 변경';
    kind.setAttribute('role', 'button');
    if (layer.color) kind.style.color = layer.color;
    kind.appendChild(icon(KIND_ICON[layer.kind] ?? 'kind-shape', { title: layer.kind }));
    kind.addEventListener('click', (e) => {
      e.stopPropagation();
      openLayerEditPopover(kind, layer, 'color');
    });

    const name = document.createElement('span');
    name.className = 'tl-row-name';
    name.textContent = layer.name;

    const actions = document.createElement('span');
    actions.className = 'tl-row-actions';

    // Up / down reorder. The list is drawn front-to-back (reversed array), so
    // "up" = toward the front = bringForward, "down" = toward the back. These
    // are the reliable path; native drag-drop reorder is also wired but flaky.
    const up = mkBtn('▲', (e) => {
      e.stopPropagation();
      bringForward(layer.id);
    }, 'tl-row-act');
    up.title = '위로';
    const down = mkBtn('▼', (e) => {
      e.stopPropagation();
      sendBackward(layer.id);
    }, 'tl-row-act');
    down.title = '아래로';

    const editBtn = mkBtn('', (e) => {
      e.stopPropagation();
      openLayerEditPopover(editBtn, layer, 'name');
    }, 'tl-row-act');
    editBtn.title = '수정 — 이름·색상';
    editBtn.appendChild(icon('edit'));

    const eye = mkBtn('', (e) => {
      e.stopPropagation();
      toggleLayerVisible(layer.id);
    }, 'tl-row-act');
    eye.title = '표시/숨김';
    eye.appendChild(icon(layer.visible === false ? 'eye-off' : 'eye'));

    const dup = mkBtn('', (e) => {
      e.stopPropagation();
      duplicateLayer(layer.id);
    }, 'tl-row-act');
    dup.title = '복제';
    dup.appendChild(icon('copy'));

    const del = mkBtn('', (e) => {
      e.stopPropagation();
      removeLayer(layer.id);
    }, 'tl-row-act danger');
    del.title = '삭제';
    del.appendChild(icon('close'));

    actions.append(up, down, editBtn, eye, dup, del);
    r.append(kind, name, actions);

    r.addEventListener('click', () => setSelectedId(layer.id));
    r.addEventListener('contextmenu', (e) => {
      setSelectedId(layer.id);
      openContextMenu(e, buildLayerMenu(layer.id));
    });

    r.addEventListener('dragstart', (ev) => {
      ev.dataTransfer?.setData('text/plain', layer.id);
      r.classList.add('dragging');
    });
    r.addEventListener('dragend', () => r.classList.remove('dragging'));
    r.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      r.classList.add('drag-over');
    });
    r.addEventListener('dragleave', () => r.classList.remove('drag-over'));
    r.addEventListener('drop', (ev) => {
      ev.preventDefault();
      r.classList.remove('drag-over');
      const draggedId = ev.dataTransfer?.getData('text/plain');
      if (!draggedId || draggedId === layer.id) return;
      const targetIdx = allLayers.findIndex((l) => l.id === layer.id);
      if (targetIdx < 0) return;
      reorderLayer(draggedId, targetIdx);
    });
    return r;
  }

  // ─── Bar + keyframes ─────────────────────────────────────────────────
  private buildBar(layer: Layer, selected: boolean, px: number): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'tl-bar' + (selected ? ' selected' : '');
    bar.dataset.id = layer.id;
    bar.style.left = `${layer.start * px}px`;
    bar.style.width = `${(layer.end - layer.start) * px}px`;
    if (layer.color) {
      bar.style.background = hexA(layer.color, 0.45);
      bar.style.borderColor = layer.color;
    }

    const label = document.createElement('span');
    label.className = 'tl-bar-label';
    label.textContent = layer.name;
    bar.appendChild(label);

    const lh = document.createElement('div');
    lh.className = 'resize l';
    lh.dataset.id = layer.id;
    lh.dataset.mode = 'left';
    const rh = document.createElement('div');
    rh.className = 'resize r';
    rh.dataset.id = layer.id;
    rh.dataset.mode = 'right';
    bar.append(lh, rh);

    const kfs = layer.keyframes ?? [];
    for (const kf of kfs) {
      const m = document.createElement('div');
      m.className = 'tl-kf';
      const within = kf.t >= layer.start && kf.t <= layer.end;
      m.classList.toggle('outside', !within);
      m.style.left = `${(kf.t - layer.start) * px}px`;
      m.title = `키프레임 ${formatTimeLabel(kf.t)} · ${kf.ease}`;
      m.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        this.kfDrag = {
          layerId: layer.id,
          kfId: kf.id,
          origT: kf.t,
          startMouseX: e.clientX,
          marker: m,
          bar,
          pendingT: kf.t,
          moved: false,
        };
      });
      m.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openContextMenu(e, [
          {
            label: '재생 헤드를 이 시간으로',
            onClick: () => setPlayhead(kf.t),
          },
          {},
          {
            label: '이 키프레임 삭제',
            danger: true,
            onClick: () => removeKeyframe(layer.id, kf.id),
          },
        ]);
      });
      bar.appendChild(m);
    }
    return bar;
  }

  // ─── Interaction ─────────────────────────────────────────────────────
  private attachInteraction(): void {
    // Playhead head — drag to scrub
    this.playheadHead.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      this.phDrag = {
        startMouseX: e.clientX,
        origT: getState().playhead,
        pendingT: getState().playhead,
      };
    });

    // Ruler click — also scrubs playhead
    this.ruler.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const px = getState().timelineZoom;
      const rect = this.tracksWrap.getBoundingClientRect();
      const x = e.clientX - rect.left + this.tracksWrap.scrollLeft;
      let ms = Math.max(0, x / px);
      ms = quantizeTime(ms, e.shiftKey);
      setPlayhead(ms);
      // Continue scrubbing while mouse is down
      this.phDrag = { startMouseX: e.clientX, origT: ms, pendingT: ms };
    });

    this.tracksWrap.addEventListener('contextmenu', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('tl-kf')) return;
      const bar = target.closest('.tl-bar') as HTMLElement | null;
      if (bar?.dataset.id) {
        setSelectedId(bar.dataset.id);
        openContextMenu(e, buildTimelineBarMenu(bar.dataset.id));
      } else {
        // empty timeline area (ruler, gaps between bars)
        openContextMenu(e, buildTimelineEmptyMenu());
      }
    });

    this.tracks.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      const bar = target.closest('.tl-bar') as HTMLElement | null;
      const handle = target.closest('.resize') as HTMLElement | null;
      const id = (bar?.dataset.id ?? handle?.dataset.id) as string | undefined;

      if (id && bar) {
        setSelectedId(id);
        const layer = getState().project.layers.find((l) => l.id === id);
        if (!layer) return;
        // setSelectedId triggered a render that detached `bar`; re-query the
        // freshly built one so subsequent style mutations land on the visible
        // DOM. Without this the user drags but nothing visually moves.
        const liveBar = this.tracks.querySelector(
          `.tl-bar[data-id="${id}"]`,
        ) as HTMLElement | null;
        const dragBar = liveBar ?? bar;
        const mode = (handle?.dataset.mode as 'left' | 'right' | undefined) ?? 'shift';
        const px = getState().timelineZoom;
        const wrapRect = this.tracksWrap.getBoundingClientRect();
        const cursorMs = (e.clientX - wrapRect.left + this.tracksWrap.scrollLeft) / px;
        this.barDrag = {
          layerId: id,
          mode,
          grabOffsetMs: cursorMs - layer.start,
          origStart: layer.start,
          origEnd: layer.end,
          bar: dragBar,
          pendingStart: layer.start,
          pendingEnd: layer.end,
        };
        dragBar.style.cursor = 'grabbing';
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Click on empty track = scrub playhead + start playhead drag
      const px = getState().timelineZoom;
      const rect = this.tracksWrap.getBoundingClientRect();
      const x = e.clientX - rect.left + this.tracksWrap.scrollLeft;
      let ms = Math.max(0, x / px);
      ms = quantizeTime(ms, e.shiftKey);
      setPlayhead(ms);
      this.phDrag = { startMouseX: e.clientX, origT: ms, pendingT: ms };
    });

    window.addEventListener('mousemove', (e) => {
      const px = getState().timelineZoom;
      // Holding Shift forces snap-to-frame even when the quantize toggle is
      // off — gives the user an ad-hoc "snap just for this drag" gesture.
      const snap = e.shiftKey;

      // Playhead drag
      const ph = this.phDrag;
      if (ph) {
        const dxMs = (e.clientX - ph.startMouseX) / px;
        let t = Math.max(0, ph.origT + dxMs);
        t = quantizeTime(t, snap);
        const dur = getState().project.duration;
        t = Math.min(t, dur);
        ph.pendingT = t;
        setPlayhead(t);
        return;
      }

      // Keyframe drag
      const k = this.kfDrag;
      if (k) {
        const dx = e.clientX - k.startMouseX;
        if (!k.moved && Math.abs(dx) >= KF_DRAG_THRESHOLD_PX) {
          k.moved = true;
          k.marker.classList.add('dragging');
        }
        if (k.moved) {
          const layer = getState().project.layers.find((l) => l.id === k.layerId);
          if (!layer) return;
          const dxMs = dx / px;
          let tNext = clamp(k.origT + dxMs, layer.start, layer.end);
          tNext = quantizeTime(tNext, snap);
          k.pendingT = tNext;
          k.marker.style.left = `${(tNext - layer.start) * px}px`;
        }
        return;
      }

      // Bar drag — cursor-anchored: the grabbed pixel of the bar follows
      // the mouse exactly (subject to quantize / clamp). No more drift when
      // the user grabs near the edge.
      const s = this.barDrag;
      if (!s) return;
      const wrapRect = this.tracksWrap.getBoundingClientRect();
      const cursorMs = (e.clientX - wrapRect.left + this.tracksWrap.scrollLeft) / px;
      const dur = getState().project.duration;

      if (s.mode === 'shift') {
        const span = s.origEnd - s.origStart;
        let ns = clamp(cursorMs - s.grabOffsetMs, 0, dur - span);
        ns = quantizeTime(ns, snap);
        s.pendingStart = ns;
        s.pendingEnd = ns + span;
      } else if (s.mode === 'left') {
        let ns = clamp(cursorMs, 0, s.origEnd - 100);
        ns = quantizeTime(ns, snap);
        s.pendingStart = ns;
        s.pendingEnd = s.origEnd;
      } else {
        let ne = clamp(cursorMs, s.origStart + 100, dur);
        ne = quantizeTime(ne, snap);
        s.pendingStart = s.origStart;
        s.pendingEnd = ne;
      }

      // Re-resolve the live bar by id every frame — if a render mid-drag
      // (autoKeyframe, audio playback, etc.) replaced the DOM, our cached
      // s.bar would be detached and the user's drag would silently no-op.
      const liveBar = this.tracks.querySelector(
        `.tl-bar[data-id="${s.layerId}"]`,
      ) as HTMLElement | null;
      const target = liveBar ?? s.bar;
      target.style.left = `${s.pendingStart * px}px`;
      target.style.width = `${(s.pendingEnd - s.pendingStart) * px}px`;
      s.bar = target;
    });

    window.addEventListener('mouseup', () => {
      // Playhead drag end
      if (this.phDrag) {
        this.phDrag = null;
        return;
      }

      // Keyframe drag commit
      const k = this.kfDrag;
      this.kfDrag = null;
      if (k) {
        k.marker.classList.remove('dragging');
        if (k.moved && Math.abs(k.pendingT - k.origT) >= 1) {
          updateKeyframe(k.layerId, k.kfId, { t: Math.round(k.pendingT) });
        } else {
          setPlayhead(k.origT);
        }
        return;
      }

      // Bar drag commit
      const s = this.barDrag;
      this.barDrag = null;
      if (!s) return;
      s.bar.style.cursor = '';
      if (s.pendingStart !== s.origStart || s.pendingEnd !== s.origEnd) {
        updateLayer(s.layerId, {
          start: Math.round(s.pendingStart),
          end: Math.round(s.pendingEnd),
        });
      }
    });

    // Mirror vertical scroll between the side labels and the tracks pane —
    // they live in independent scroll containers, so without this they would
    // drift apart when many layers exist. Setting scrollTop to the same value
    // does not re-fire `scroll`, so this loop terminates naturally.
    this.tracksWrap.addEventListener('scroll', () => {
      if (this.side.scrollTop !== this.tracksWrap.scrollTop) {
        this.side.scrollTop = this.tracksWrap.scrollTop;
      }
    });
    this.side.addEventListener('scroll', () => {
      if (this.tracksWrap.scrollTop !== this.side.scrollTop) {
        this.tracksWrap.scrollTop = this.side.scrollTop;
      }
    });

    // Wheel default = zoom; Shift/Ctrl/Cmd hold = scroll instead.
    //   • Shift  → use deltaY as HORIZONTAL scroll (mouse-wheel-only setups)
    //   • Ctrl/⌘ → let the native scroll through (vertical or horizontal)
    //   • Trackpad horizontal swipe (deltaX dominant) is always native scroll
    this.tracksWrap.addEventListener('wheel', (e) => {
      // Trackpad horizontal swipe → never zoom
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      if (e.shiftKey) {
        e.preventDefault();
        this.tracksWrap.scrollLeft += e.deltaY;
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        // Let the browser handle scrolling natively
        return;
      }
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      setTimelineZoom(getState().timelineZoom * factor);
    }, { passive: false });
  }
}

// ─── helpers ───────────────────────────────────────────────────────────
function clamp(x: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, x));
}

/** Tiny popover anchored to a side-row button, lets the user rename the
 *  layer and pick a tint without opening the full inspector. */
let openEditPopover: { close: () => void } | null = null;
function openLayerEditPopover(anchor: HTMLElement, layer: Layer, focus: 'name' | 'color'): void {
  if (openEditPopover) {
    openEditPopover.close();
    openEditPopover = null;
  }

  const pop = document.createElement('div');
  pop.className = 'popover layer-edit-popover';
  pop.addEventListener('mousedown', (e) => e.stopPropagation());
  pop.addEventListener('click', (e) => e.stopPropagation());

  const title = document.createElement('div');
  title.className = 'popover-title';
  title.textContent = '레이어 수정';
  pop.appendChild(title);

  // ─── Name ───
  const nameField = document.createElement('div');
  nameField.className = 'popover-field';
  const nameLabel = document.createElement('label');
  nameLabel.className = 'popover-label';
  nameLabel.textContent = '이름';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.value = layer.name;
  nameInput.addEventListener('input', () => updateLayer(layer.id, { name: nameInput.value }));
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      close();
    }
  });
  nameField.append(nameLabel, nameInput);
  pop.appendChild(nameField);

  // ─── Color palette ───
  const colorField = document.createElement('div');
  colorField.className = 'popover-field';
  const colorLabel = document.createElement('label');
  colorLabel.className = 'popover-label';
  colorLabel.textContent = '색상';
  const palette = document.createElement('div');
  palette.className = 'lcp-row';
  const colors = [...FULL_PALETTE, ...getCustomSwatches()];
  for (const c of colors) {
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'lcp-sw';
    sw.style.background = c;
    if (c === layer.color) sw.classList.add('active');
    sw.title = c;
    sw.dataset.color = c;
    palette.appendChild(sw);
  }
  // Drag-through-swatches + click both apply.
  const applyAt = (clientX: number, clientY: number): void => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const swEl = el?.closest('.lcp-sw') as HTMLElement | null;
    if (swEl?.dataset.color) {
      updateLayer(layer.id, { color: swEl.dataset.color });
      palette.querySelectorAll('.lcp-sw').forEach((b) => {
        (b as HTMLElement).classList.toggle('active', (b as HTMLElement).dataset.color === swEl.dataset.color);
      });
    }
  };
  palette.addEventListener('pointerdown', (e) => {
    const swEl = (e.target as HTMLElement).closest('.lcp-sw') as HTMLElement | null;
    if (!swEl?.dataset.color) return;
    e.preventDefault();
    applyAt(e.clientX, e.clientY);
    const move = (ev: PointerEvent): void => applyAt(ev.clientX, ev.clientY);
    const up = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  });

  const customRow = document.createElement('div');
  customRow.className = 'lcp-row';
  const customColor = document.createElement('input');
  customColor.type = 'color';
  customColor.className = 'lcp-custom';
  customColor.value = layer.color && /^#[0-9a-fA-F]{6}$/.test(layer.color) ? layer.color : '#888888';
  customColor.title = '직접 색상 추가';
  customColor.addEventListener('input', () => updateLayer(layer.id, { color: customColor.value }));
  customColor.addEventListener('change', () => addCustomSwatch(customColor.value));
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'lcp-clear';
  clearBtn.textContent = '색 없음';
  clearBtn.addEventListener('click', () => updateLayer(layer.id, { color: undefined }));
  customRow.append(customColor, clearBtn);

  colorField.append(colorLabel, palette, customRow);
  pop.appendChild(colorField);

  // ─── More: jump to inspector ───
  const more = document.createElement('button');
  more.type = 'button';
  more.className = 'btn ghost popover-exit';
  more.textContent = '⚙ 인스펙터에서 자세히 →';
  more.addEventListener('click', () => {
    setSelectedId(layer.id);
    document.body.classList.remove('inspector-collapsed');
    document.querySelector('.app')?.classList.remove('inspector-collapsed');
    close();
  });
  pop.appendChild(more);

  document.body.appendChild(pop);
  // Position next to the anchor — flip if it would overflow the right edge.
  const r = anchor.getBoundingClientRect();
  pop.style.position = 'fixed';
  pop.style.top = `${r.bottom + 6}px`;
  const desiredLeft = r.left;
  const popWidth = pop.offsetWidth || 240;
  const maxLeft = window.innerWidth - popWidth - 8;
  pop.style.left = `${Math.max(8, Math.min(desiredLeft, maxLeft))}px`;

  if (focus === 'name') setTimeout(() => nameInput.select(), 0);

  const onDoc = (e: MouseEvent): void => {
    const t = e.target as Node;
    if (pop.contains(t) || anchor.contains(t)) return;
    close();
  };
  function close(): void {
    pop.remove();
    document.removeEventListener('mousedown', onDoc);
    openEditPopover = null;
  }
  setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
  openEditPopover = { close };
}

function promptGroupRename(groupId: string, current: string): void {
  const next = window.prompt('그룹 이름', current);
  if (next == null || next.trim() === '') return;
  renameGroup(groupId, next.trim());
}

/** Convert "#rrggbb" or "#rgb" to an "rgba(...)" string with the given alpha. */
function hexA(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(v.slice(0, 2), 16) || 0;
  const g = parseInt(v.slice(2, 4), 16) || 0;
  const b = parseInt(v.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
function formatTimeLabel(ms: number): string {
  const total = Math.max(0, Math.round(ms));
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const mmm = total % 1000;
  return `${m}:${String(s).padStart(2, '0')}.${String(mmm).padStart(3, '0')}`;
}
function mkBtn(
  text: string,
  onClick: (e: MouseEvent) => void,
  className = 'btn',
): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = className;
  if (text) b.textContent = text;
  b.addEventListener('click', onClick);
  return b;
}
function sep(): HTMLElement {
  const s = document.createElement('div');
  s.className = 'tl-tb-sep';
  return s;
}
